// Organizasyon teklifi PDF'i (nişan, toplantı, mezuniyet gecesi …) — oda teklifi PDF'iyle AYNI görünüm.
// Görünüm parçaları (styles, policyStyles) ve Roboto font kaydı ../offers/OfferPDF'ten gelir.
//
// Sayfa düzeni: antetli arka planın üst ~60pt'si logolar, alt ~128pt'si (≈714pt'den itibaren) otel fotoğrafı.
// Page'e üst/alt boşluk verilir → çok kalemli tekliflerde içerik sonraki sayfalara akar ve hiçbir sayfada
// logoların ya da fotoğraf bandının üstüne binmez. 1. sayfanın (ve koşullar sayfasının) başlığı negatif
// marginTop ile oda teklifindeki gibi en üste çekilir.
// Tipik teklif (3 kalem + indirim + KDV + kapora + kısa not + banka) ana içeriği TEK sayfaya sığsın diye:
// ödeme planı ayrı bölüm değil, toplamlar bloğunun SOLUNDA durur; kısa notlar banka bölümünün sağ sütununa,
// geçerlilik metninin altına yazılır (uzun notlar ayrı bölüm olarak kalır).
// react-pdf `textTransform: 'uppercase'` Türkçe bilmez ("i" → "I"): büyük harf başlıklar HAZIR verilir.
import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { styles, policyStyles } from '../offers/OfferPDF';
import { formatMoneyTR, discountLabel, DEFAULT_DISCOUNT_NAME } from '../../utils/pricing';
import {
  eventOfferTotals, eventLineTotal, toEventNumber, itemVatRate, visibleVatGroups, vatRatesText, hasZeroVatItems,
} from '../../utils/eventPricing';
import { parseTermsText, splitTitleLines } from './eventTerms';

const PAGE_PAD_TOP = 82;      // logoların altı (logolar ≈58pt'de bitiyor)
const PAGE_PAD_BOTTOM = 134;  // fotoğraf bandının üstü (bant ≈714pt'de başlıyor; içerik ≤708pt)
const HEADER_TOP = 15;        // oda teklifindeki başlık konumu (contentContainer paddingTop)
const A4_HEIGHT = 841.89;
const VIEW_BASE_URL = 'https://crm.ramadaergan.com/offers/view/';
const DEFAULT_HOTEL_NAME = 'Ramada by Wyndham Ergan Kayak Merkezi';

const ev = {
  // Mutlak konumlu öğelerde top 0 = sayfanın üst kenarı, ama yüzde yükseklik dolgulu içerik alanına göre
  // hesaplanır → arka plan yüksekliği pt olarak verilir (oda teklifindeki %100,5 ile aynı).
  page: { paddingTop: PAGE_PAD_TOP, paddingBottom: PAGE_PAD_BOTTOM },
  background: { height: A4_HEIGHT * 1.005 },
  body: { paddingHorizontal: 30 },
  firstHeader: { marginTop: -(PAGE_PAD_TOP - HEADER_TOP) },
  // Sağdaki başlık kapsayıcısı Erzincan logosuna (≈340pt'de bitiyor) binmesin: uzun başlık sağa yaslı satırlara kayar
  headerRight: { maxWidth: 215 },
  headerText: { textAlign: 'right' },
  // Bölümler arası boşluk marginBottom DEĞİL marginTop ile: react-pdf, sayfaya sığan bir bölümün
  // marginBottom'ı sayfa sonunu aşınca bölümün TAMAMINI sonraki sayfaya atıyor (1. sayfada büyük boşluk).
  sectionFirst: { marginBottom: 0 },
  sectionNext: { marginBottom: 0, marginTop: 10 },
  // Akan bölümlerin (kalemler, notlar) başlığı bölümün DIŞINDA durur: react-pdf minPresenceAhead'i yalnızca
  // önünde kardeşi olan öğede uygular → başlık, ardından gelen satırlar sığmıyorsa onlarla birlikte taşınır.
  titleNext: { marginTop: 14 },
  termsFirst: { marginBottom: 0 },
  termsNext: { marginBottom: 0, marginTop: 7 },
  pageMark: {
    position: 'absolute', top: 64, left: 30, right: 30,
    fontSize: 6.5, color: '#9CA3AF', textAlign: 'right',
  },
  infoLabel: { width: 78, fontSize: 7.5, color: '#4B5563', fontWeight: 'bold' },
  infoValue: { flex: 1, fontSize: 7.5, color: '#111827' },
  detailCell: { width: '50%', flexDirection: 'row', paddingVertical: 2.5, borderBottomWidth: 0.5, borderBottomColor: '#F3F4F6' },
  detailLabel: { width: 82, fontSize: 7.5, color: '#4B5563', fontWeight: 'bold', paddingTop: 1 },
  detailValue: { flex: 1, fontSize: 9, color: '#111827', paddingRight: 8 },
  thRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#E5E7EB', paddingBottom: 4, marginBottom: 4 },
  th: { fontSize: 9.5, color: '#111827', fontWeight: 'bold' },
  itemRow: { flexDirection: 'row', marginBottom: 4, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: '#F3F4F6' },
  colName: { flex: 4.6, paddingRight: 8 },
  colQty: { flex: 1.3, textAlign: 'center' },
  colUnit: { flex: 1.4, textAlign: 'right' },
  colTotal: { flex: 1.5, textAlign: 'right' },
  // Kalemlerin KDV oranları farklıysa (yemek %10, hizmet %20) "KDV" sütunu eklenir; yer kalem adından ve adetten alınır
  colNameVat: { flex: 4.4 },
  colQtyVat: { flex: 1.15 },
  colVat: { flex: 0.75, textAlign: 'right' },
  itemName: { fontSize: 9.5, color: '#111827' },
  itemDesc: { fontSize: 7.5, color: '#6B7280', marginTop: 1, lineHeight: 1.35 },
  cell: { fontSize: 9.5, color: '#111827' },
  // Son kalemin altı: solda ödeme planı (kapora), sağda toplamlar — tek satır, bölünmez
  summary: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 2 },
  totals: { width: 270 },
  pay: { flex: 1, paddingRight: 14 },
  payTitle: {
    fontSize: 8, fontWeight: 'bold', color: '#111827',
    paddingBottom: 2, marginBottom: 1, borderBottomWidth: 0.5, borderBottomColor: '#E5E7EB',
  },
  sumRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 1.5 },
  sumLabel: { flex: 1, fontSize: 8, color: '#4B5563', textAlign: 'right', paddingRight: 10 },
  sumValue: { width: 110, fontSize: 8, color: '#4B5563', textAlign: 'right' },
  discount: { color: '#B91C1C', fontWeight: 'bold' },
  grandRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 2, paddingTop: 3, borderTopWidth: 0.5, borderTopColor: '#9CA3AF' },
  grandLabel: { flex: 1, fontSize: 10.5, color: '#111827', fontWeight: 'bold', textAlign: 'right', paddingRight: 10 },
  grandValue: { width: 110, fontSize: 10.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' },
  vatNote: { fontSize: 7, color: '#6B7280', textAlign: 'right', marginTop: 2 },
  payRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 2, borderBottomWidth: 0.5, borderBottomColor: '#F3F4F6' },
  payLabel: { width: 60, fontSize: 8, color: '#4B5563', fontWeight: 'bold' },
  payAmount: { width: 76, fontSize: 8.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' },
  payDue: { flex: 1, fontSize: 7.5, color: '#6B7280', paddingLeft: 8 },
  notes: { fontSize: 8, color: '#374151', lineHeight: 1.4 },
  // Kısa notlar banka bölümünün sağ sütununda (geçerlilik metninin altında)
  bankNotesTitle: { marginTop: 5 },
  bankNotes: { fontSize: 7.5, color: '#374151', lineHeight: 1.4 },
};

// ─── Biçimlendirme ─────────────────────────────────────────────────────────────
const LONG_DATE = { day: 'numeric', month: 'long', year: 'numeric' };

// 'YYYY-MM-DD' yerel tarih olarak kurulur (UTC kayması olmasın); ISO/Date olduğu gibi okunur.
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(/^\d{4}-\d{2}-\d{2} \d/.test(s) ? s.replace(' ', 'T') : s);
  return Number.isNaN(d.getTime()) ? null : d;
};
const formatDate = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('tr-TR', LONG_DATE) : '';
};
const formatDateWithDay = (value) => {
  const d = parseDate(value);
  return d ? `${d.toLocaleDateString('tr-TR', LONG_DATE)}, ${d.toLocaleDateString('tr-TR', { weekday: 'long' })}` : '';
};
const formatNumber = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
const upperTR = (s) => String(s || '').toLocaleUpperCase('tr-TR');

const timeText = (start, end) => {
  if (start && end) return `${start} – ${end}`;
  if (start) return `Başlangıç: ${start}`;
  if (end) return `Bitiş: ${end}`;
  return '';
};

const quantityText = (item) => {
  const q = toEventNumber(item?.quantity);
  if (!Number.isFinite(q)) return '-';
  return [formatNumber(q), String(item?.unit || '').trim()].filter(Boolean).join(' ');
};

const offerNo = (offer) => (offer?.id ? `ORG-${offer.id}` : '');
const offerDateText = (offer) => formatDate(offer?.createdAt) || new Date().toLocaleDateString('tr-TR', LONG_DATE);

// ─── Parçalar ──────────────────────────────────────────────────────────────────
const Background = ({ settings }) => (settings?.pdfBackground ? (
  <Image src={settings.pdfBackground} style={[styles.backgroundImage, ev.background]} fixed />
) : null);

// 2.+ sayfalarda logoların altında küçük teklif künyesi (1. sayfada başlık zaten var)
const ContinuationMark = ({ text }) => (
  <Text
    style={ev.pageMark}
    fixed
    render={({ pageNumber, totalPages }) => (pageNumber > 1 ? `${text}  ·  Sayfa ${pageNumber} / ${totalPages}` : '')}
  />
);

const InfoRow = ({ label, value }) => (
  <View style={styles.row}>
    <Text style={ev.infoLabel}>{label}</Text>
    <Text style={ev.infoValue}>{value}</Text>
  </View>
);

const SumRow = ({ label, value, danger }) => (
  <View style={ev.sumRow}>
    <Text style={danger ? [ev.sumLabel, ev.discount] : ev.sumLabel}>{label}</Text>
    <Text style={danger ? [ev.sumValue, ev.discount] : ev.sumValue}>{value}</Text>
  </View>
);

// Bölünmez (wrap={false}) öğe bir sayfadan uzun olursa react-pdf onu sayfaya sığdırmadan basar ve fotoğraf
// bandına taşar. Bu yüzden yalnızca makul uzunluktaki metinler bir arada tutulur; çok uzunlar satır satır akar.
const descLineCount = (text, charsPerLine = 70) => String(text || '').split('\n')
  .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);
const fitsOnPage = (text, charsPerLine = 70, maxLines = 30) => descLineCount(text, charsPerLine) <= maxLines;

// Yükseklik tahminleri (pt) — react-pdf çıktısında ölçülerek ayarlandı: lineHeight verilmeyen Roboto satırı
// ≈ 1,32 × punto (9,5pt kalem adı ≈ 12,5pt), açıklama satırı 7,5 × 1,35 ≈ 10,1pt. Açıklama satır sayısı 70
// karakter/satırla tahmin edilir (gerçekte ~80): tahmin fazla çıkar → yetim başlık olmaz, en kötü erken taşınır.
const TABLE_HEAD_HEIGHT = 21; // "Kalem / Adet / Birim Fiyat / Toplam" satırı + alt boşluğu
// KDV sütunu varken kalem adı sütunu ~%9 dar → satır başına daha az karakter
const NAME_CHARS = 46;
const NAME_CHARS_VAT = 42;
const DESC_CHARS = 70;
const DESC_CHARS_VAT = 64;
const estimateItemHeight = (item, vatCol = false) => (item
  ? 4.5 + descLineCount(item.name || '-', vatCol ? NAME_CHARS_VAT : NAME_CHARS) * 12.6
    + (item.description ? 1 + descLineCount(item.description, vatCol ? DESC_CHARS_VAT : DESC_CHARS) * 10.15 : 0)
  : 0);

// Kısa not (≈4 satıra kadar) banka bölümünün sağ sütununa sığar; uzunu ayrı NOTLAR bölümü olur.
// Sağ sütun ≈257pt, 7,5pt yazıda ~76 karakter/satır (tahmin hafif fazla sayar).
const NOTE_CHARS_PER_LINE = 72;
const SHORT_NOTE_LINES = 4;

// vatRate: kalemin oranı (KDV sütunu yoksa null)
const ItemRow = ({ item, money, vatRate = null }) => {
  const vatCol = vatRate !== null;
  return (
    <View style={ev.itemRow} wrap={!fitsOnPage(item?.description, vatCol ? DESC_CHARS_VAT : DESC_CHARS)}>
      <View style={vatCol ? [ev.colName, ev.colNameVat] : ev.colName}>
        <Text style={ev.itemName}>{item?.name || '-'}</Text>
        {item?.description ? (
          <Text style={ev.itemDesc}>{String(item.description).replace(/\r\n/g, '\n')}</Text>
        ) : null}
      </View>
      <Text style={vatCol ? [ev.cell, ev.colQty, ev.colQtyVat] : [ev.cell, ev.colQty]}>{quantityText(item)}</Text>
      <Text style={[ev.cell, ev.colUnit]}>{money(toEventNumber(item?.unitPrice) || 0)}</Text>
      {vatCol ? <Text style={[ev.cell, ev.colVat]}>%{formatNumber(vatRate)}</Text> : null}
      <Text style={[ev.cell, ev.colTotal, { fontWeight: 'bold' }]}>{money(eventLineTotal(item))}</Text>
    </View>
  );
};

// ─── Son sayfa: organizasyon koşulları (Ayarlar > Organizasyon → termsText) ───
// OfferPDF'teki CancellationPolicyPage ile aynı sade tasarım.
const TITLE_LINE_CHARS = 30;                 // 11,5pt kalın büyük harf ≈ 7pt/karakter → 215pt'ye ~30 karakter
// react-pdf'in İngilizce heceleme kuralı başlığı "OR-/GANİZASYONLARI" diye bölmesin; yalnızca satıra hiç sığmayacak
// kadar uzun tek "kelime" (ör. "REZERVASYON/ÖDEME/İPTAL/…") hecelenir, yoksa sağ kenardan taşar.
const noHyphenation = (word, builtinHyphenate) => (word.length <= TITLE_LINE_CHARS ? [word] : builtinHyphenate(word));
const groupTermBlocks = (blocks) => {
  const groups = [];
  blocks.forEach((b) => {
    if (b.type === 'heading' || groups.length === 0) groups.push({ heading: b.type === 'heading' ? b.text : '', items: [] });
    if (b.type !== 'heading') groups[groups.length - 1].items.push(b);
  });
  return groups;
};

const TermBlock = ({ block }) => (block.type === 'bullet' ? (
  <View style={policyStyles.listItem} wrap={!fitsOnPage(block.text, 100)}>
    <Text style={policyStyles.bullet}>•</Text>
    <Text style={policyStyles.listText}>{block.text}</Text>
  </View>
) : (
  <Text style={[policyStyles.body, { marginBottom: 3 }]}>{block.text}</Text>
));

const EventTermsPage = ({ offer, settings, eventSettings }) => {
  const terms = parseTermsText(eventSettings?.termsText);
  // Başlık iki satıra bölünür (oda teklifiyle aynı görünüm); yarılardan biri 215pt'lik kapsayıcıya sığmayacak kadar
  // uzunsa zorla bölünmez, kendiliğinden sağa yaslı satırlara akar (yarım satır + tek kelimelik satır olmasın)
  const titleLines = splitTitleLines(upperTR(terms.title));
  const titleText = titleLines.join(titleLines.every((line) => line.length <= TITLE_LINE_CHARS) ? '\n' : ' ');
  const hotelName = settings?.hotelName || DEFAULT_HOTEL_NAME;
  const offerRef = [offer?.customerName, offerNo(offer), `Teklif Tarihi: ${offerDateText(offer)}`].filter(Boolean).join('  ·  ');
  const groups = groupTermBlocks(terms.blocks);

  return (
    <Page size="A4" style={[styles.page, ev.page]}>
      <Background settings={settings} />
      <View style={ev.body}>
        <View style={[styles.header, ev.firstHeader]}>
          <View />
          {/* Başlık Ayarlar'dan serbest metin: genişlik sınırı olmazsa uzun başlık Erzincan logosuna biner */}
          <View style={[styles.headerRight, ev.headerRight]}>
            <Text style={styles.title} hyphenationCallback={noHyphenation}>{titleText}</Text>
            <Text style={[styles.subtitle, ev.headerText]}>{hotelName}</Text>
            <Text style={[policyStyles.headerRef, ev.headerText]}>{offerRef}</Text>
          </View>
        </View>

        {groups.map((group, gi) => {
          const [first, ...rest] = group.items;
          return (
            <View key={gi} style={[policyStyles.section, gi > 0 ? ev.termsNext : ev.termsFirst]}>
              {/* Bölüm başlığı ilk maddeyle birlikte: başlık sayfa sonunda tek başına kalmaz.
                  (minPresenceAhead burada işe yaramıyor — react-pdf ebeveynin ilk çocuğunu hiç taşımıyor.) */}
              {group.heading ? (
                <View wrap={!(first === undefined || fitsOnPage(first.text, 100, 20))}>
                  <Text style={styles.sectionTitle}>{upperTR(group.heading)}</Text>
                  {first !== undefined && <TermBlock block={first} />}
                </View>
              ) : (first !== undefined && <TermBlock block={first} />)}
              {rest.map((block, i) => <TermBlock key={i} block={block} />)}
            </View>
          );
        })}
      </View>
    </Page>
  );
};

// ─── Ana bileşen ───────────────────────────────────────────────────────────────
// offer: sunucu GET biçimi (kalemler sayısal). settings: { ...logos, bank }. eventSettings: fetchEventSettings().
// internalNote (iç not) bilinçli olarak HİÇ basılmaz.
export const EventPDF = ({ offer, settings, eventSettings }) => {
  const o = offer || {};
  const items = Array.isArray(o.items) ? o.items : [];
  const lastItem = items.length > 0 ? items[items.length - 1] : null;
  const currency = o.currency || 'TL';
  const money = (n) => `${formatMoneyTR(n)} ${currency}`;
  const t = eventOfferTotals(o);

  const hotelName = settings?.hotelName || DEFAULT_HOTEL_NAME;
  const bank = settings?.bank || {};
  const hasBankInfo = !!(bank.bankName || bank.accountHolder || bank.tlIban || bank.euroIban || bank.usdIban);

  // Müşteri
  const taxValue = [o.taxOffice, o.taxNumber].filter(Boolean).join(' / ');
  const taxLabel = o.taxOffice && o.taxNumber ? 'Vergi Dairesi / No:' : (o.taxOffice ? 'Vergi Dairesi:' : 'Vergi No:');
  const customerRows = [
    ['Yetkili:', o.customerName],
    ['Firma:', o.companyName],
    ['Telefon:', o.phone],
    ['E-posta:', o.email],
    [taxLabel, taxValue],
    ['Adres:', o.address],
  ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');

  // Etkinlik
  const guests = Number(o.guestCount);
  const detailRows = [
    ['Organizasyon Türü', o.eventType],
    ['Etkinlik Adı', o.eventName],
    ['Tarih', formatDateWithDay(o.eventDate)],
    ['Saat', timeText(o.startTime, o.endTime)],
    ['Kişi Sayısı', Number.isFinite(guests) && guests > 0 ? `${formatNumber(guests)} kişi` : ''],
    ['Salon / Mekan', o.venue],
  ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');

  // Toplamlar (kayıttaki totalPrice ile aynı kural: eventPricing.computeEventTotals)
  // KDV kalem başına: hariç modda her oran ayrı satır; dahil modda "Fiyatlara %10 ve %20 KDV dahildir."
  const hasDiscount = t.discountAmount > 0;
  const vatGroups = visibleVatGroups(t);
  const vatExcluded = o.vatMode === 'excluded' && vatGroups.length > 0;
  const showSubTotal = hasDiscount || vatExcluded;
  const itemRates = items.map((it) => itemVatRate(it, o.vatRate));
  const vatCol = new Set(itemRates).size > 1;
  const discountText = discountLabel({
    name: String(o.discountName || '').trim() || DEFAULT_DISCOUNT_NAME,
    type: o.discountType,
    value: o.discountValue,
  });

  // Kapora planı
  const hasDeposit = !!o.depositType && t.depositAmount > 0;
  const depositLabel = o.depositType === 'percentage' ? `Kapora (%${formatNumber(o.depositValue)})` : 'Kapora';
  const depositDue = formatDate(o.depositDueDate);
  const balanceDue = formatDate(o.balanceDueDate);
  const validUntil = formatDate(o.validUntil);
  const notes = String(o.notes || '').replace(/\r\n/g, '\n').trim();
  const shortNotes = !!notes && descLineCount(notes, NOTE_CHARS_PER_LINE) <= SHORT_NOTE_LINES;

  // "TEKLİF KALEMLERİ" başlığının altında aynı sayfada en az tablo başlığı + ilk kalem olsun (yaklaşık yükseklik).
  // Bölünemeyen ilk kalemde pay KIRPILMAZ (kırpılırsa başlık + tablo başlığı sayfada tek başına kalır); tek kalemli
  // teklifte son kalem toplamlar satırıyla birlikte taşındığı için onun yüksekliği de eklenir.
  const firstItem = items[0];
  const descChars = vatCol ? DESC_CHARS_VAT : DESC_CHARS;
  const firstItemKept = !firstItem || fitsOnPage(firstItem.description, descChars);
  const totalsHeight = 19.5 + 12.1 * ([showSubTotal, hasDiscount].filter(Boolean).length + (vatExcluded ? vatGroups.length : 0))
    + (!vatExcluded && vatGroups.length > 0 ? 11.3 : 0);
  const payHeight = hasDeposit ? 14.1 + 15.8 * (t.balance > 0 ? 2 : 1) : 0;
  const summaryHeight = 6 + Math.max(totalsHeight, payHeight); // + kalemin alt boşluğu ve satırın üst boşluğu
  // Bölünemeyen kalemde fazla pay sayfa sonunda gereksiz boşluk bırakır, eksik pay yetim başlık → ~1,5pt emniyet payı.
  const itemsPresence = firstItemKept
    ? TABLE_HEAD_HEIGHT + 1.5 + estimateItemHeight(firstItem, vatCol) + (items.length <= 1 ? summaryHeight : 0)
    : 30 + Math.min(estimateItemHeight(firstItem, vatCol), 200);

  const markText = [offerNo(o), o.customerName].filter(Boolean).join('  ·  ');

  return (
    <Document title={`Organizasyon Teklifi ${offerNo(o)}`.trim()} author={hotelName} language="tr">
      <Page size="A4" style={[styles.page, ev.page]}>
        <Background settings={settings} />
        <ContinuationMark text={markText} />

        <View style={ev.body}>
          {/* Başlık — oda teklifiyle aynı yapı (sol taraf arka plandaki logolar) */}
          <View style={[styles.header, ev.firstHeader]}>
            <View />
            <View style={[styles.headerRight, ev.headerRight]}>
              <Text style={styles.title}>{'ORGANİZASYON\nFİYAT TEKLİFİ'}</Text>
              {offerNo(o) ? <Text style={styles.subtitle}>Teklif No: {offerNo(o)}</Text> : null}
              <Text style={styles.subtitle}>Teklif Tarihi: {offerDateText(o)}</Text>
            </View>
          </View>

          {/* Müşteri + otel bilgileri + QR */}
          <View style={[styles.section, ev.sectionFirst]} wrap={false}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <View style={{ width: '40%' }}>
                <Text style={styles.sectionTitle}>MÜŞTERİ BİLGİLERİ</Text>
                {customerRows.map(([label, value]) => <InfoRow key={label} label={label} value={value} />)}
              </View>

              <View style={{ width: '37%', marginTop: 5, paddingLeft: 10 }}>
                <Text style={{ fontSize: 9.5, fontWeight: 'bold', color: '#111827', marginBottom: 6 }}>{hotelName}</Text>
                <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ width: '30%', fontSize: 8.5, color: '#4B5563', fontWeight: 'bold' }}>Telefon:</Text>
                  <Text style={{ width: '70%', fontSize: 8.5, color: '#111827' }}>{settings?.hotelPhone || '0547 811 24 24'}</Text>
                </View>
                <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ width: '30%', fontSize: 8.5, color: '#4B5563', fontWeight: 'bold' }}>E-Posta:</Text>
                  <Text style={{ width: '70%', fontSize: 8.5, color: '#111827' }}>{settings?.hotelEmail || 'info@ramadaergan.com'}</Text>
                </View>
              </View>

              <View style={{ width: '23%', alignItems: 'flex-end', justifyContent: 'center', marginTop: 5 }}>
                {o.pdfFile ? (
                  <>
                    <Image
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(VIEW_BASE_URL + o.pdfFile)}`}
                      style={{ width: 60, height: 60 }}
                    />
                    <Text style={{ fontSize: 6.5, color: '#9CA3AF', marginTop: 2, textAlign: 'center', width: 60 }}>Online Görüntüle</Text>
                  </>
                ) : null}
              </View>
            </View>
          </View>

          {/* Etkinlik detayları — iki sütunlu ızgara, yalnızca dolu alanlar */}
          {detailRows.length > 0 && (
            <View style={[styles.section, ev.sectionNext]} wrap={false}>
              <Text style={styles.sectionTitle}>ETKİNLİK DETAYLARI</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {detailRows.map(([label, value], i) => (
                  <View key={label} style={[ev.detailCell, i % 2 === 1 ? { paddingLeft: 10 } : {}]}>
                    <Text style={ev.detailLabel}>{label}</Text>
                    <Text style={ev.detailValue}>{value}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Kalemler + toplamlar */}
          <Text style={[styles.sectionTitle, ev.titleNext]} minPresenceAhead={itemsPresence}>TEKLİF KALEMLERİ</Text>
          <View style={[styles.section, ev.sectionFirst]}>
            {/* Tablo ayrı View: başlık satırı (fixed) yalnızca kalemlerin aktığı sayfalarda tekrarlanır.
                Son kalem toplam bloğuyla birlikte tutulur → toplamlar bir sayfada tek başına kalmaz. */}
            <View>
              <View style={ev.thRow} fixed>
                <Text style={vatCol ? [ev.th, ev.colName, ev.colNameVat] : [ev.th, ev.colName]}>Kalem</Text>
                <Text style={vatCol ? [ev.th, ev.colQty, ev.colQtyVat] : [ev.th, ev.colQty]}>Adet</Text>
                <Text style={[ev.th, ev.colUnit]}>Birim Fiyat</Text>
                {vatCol ? <Text style={[ev.th, ev.colVat]}>KDV</Text> : null}
                <Text style={[ev.th, ev.colTotal]}>Toplam</Text>
              </View>

              {items.slice(0, -1).map((item, i) => (
                <ItemRow key={i} item={item} money={money} vatRate={vatCol ? itemRates[i] : null} />
              ))}

              <View wrap={!(lastItem === null || fitsOnPage(lastItem.description, descChars))}>
                {lastItem !== null && <ItemRow item={lastItem} money={money} vatRate={vatCol ? itemRates[items.length - 1] : null} />}
                <View style={ev.summary} wrap={false}>
                  {/* Ödeme planı (kapora) — toplamların solundaki boşlukta */}
                  <View style={ev.pay}>
                    {hasDeposit && (
                      <>
                        <Text style={ev.payTitle}>ÖDEME PLANI</Text>
                        <View style={[ev.payRow, t.balance > 0 ? {} : { borderBottomWidth: 0 }]}>
                          <Text style={ev.payLabel}>{depositLabel}</Text>
                          <Text style={ev.payAmount}>{money(t.depositAmount)}</Text>
                          <Text style={ev.payDue}>{depositDue ? `Son ödeme: ${depositDue}` : ''}</Text>
                        </View>
                        {t.balance > 0 && (
                          <View style={[ev.payRow, { borderBottomWidth: 0 }]}>
                            <Text style={ev.payLabel}>Kalan Tutar</Text>
                            <Text style={ev.payAmount}>{money(t.balance)}</Text>
                            <Text style={ev.payDue}>{balanceDue ? `Son ödeme: ${balanceDue}` : ''}</Text>
                          </View>
                        )}
                      </>
                    )}
                  </View>

                  <View style={ev.totals}>
                    {showSubTotal && <SumRow label="Ara Toplam" value={money(t.subTotal)} />}
                    {/* Kırmızı + kalın: oda teklifindeki indirim satırıyla aynı */}
                    {hasDiscount && <SumRow danger label={discountText} value={`-${money(t.discountAmount)}`} />}
                    {vatExcluded && vatGroups.map((g) => (
                      <SumRow key={g.rate} label={`KDV (%${formatNumber(g.rate)})`} value={money(g.vat)} />
                    ))}
                    <View style={ev.grandRow}>
                      <Text style={ev.grandLabel}>{vatExcluded ? 'Genel Toplam (KDV dahil)' : 'Genel Toplam'}</Text>
                      <Text style={ev.grandValue}>{money(t.total)}</Text>
                    </View>
                    {!vatExcluded && vatGroups.length > 0 && (
                      <Text style={ev.vatNote}>
                        Fiyatlara {vatRatesText(vatGroups, formatNumber)} KDV dahildir{hasZeroVatItems(t) ? " (%0 KDV'li kalemler hariç)" : ''}.
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Müşteriye görünen uzun notlar (iç not basılmaz) — kısa notlar banka bölümünde */}
          {notes && !shortNotes ? (
            <>
              <Text style={[styles.sectionTitle, ev.titleNext]} minPresenceAhead={36}>NOTLAR</Text>
              <View style={[styles.section, ev.sectionFirst]}>
                <Text style={ev.notes}>{notes}</Text>
              </View>
            </>
          ) : null}

          {/* Banka bilgileri + geçerlilik / koşullar yönlendirmesi */}
          <View style={[styles.bankSection, { marginTop: 10 }]} wrap={false}>
            {hasBankInfo && (
              <View style={styles.bankColumnLeft}>
                <Text style={styles.bankTitle}>BANKA HESAP BİLGİLERİ</Text>
                {[
                  ['Banka:', bank.bankName],
                  ['Hesap Sahibi:', bank.accountHolder],
                  ['TL IBAN:', bank.tlIban],
                  ['EURO IBAN:', bank.euroIban],
                  ['USD IBAN:', bank.usdIban],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <View key={label} style={styles.bankRow}>
                    <Text style={styles.bankLabel}>{label}</Text>
                    <Text style={styles.bankValue}>{value}</Text>
                  </View>
                ))}
              </View>
            )}

            <View style={hasBankInfo ? styles.bankColumnRight : { flex: 1 }}>
              <Text style={styles.bankTitle}>TEKLİF GEÇERLİLİĞİ VE KOŞULLAR</Text>
              {validUntil ? (
                <Text style={[styles.policyText, { fontSize: 7.5, marginBottom: 2 }]}>
                  Bu teklif <Text style={{ fontWeight: 'bold', color: '#111827' }}>{validUntil}</Text> tarihine kadar geçerlidir.
                </Text>
              ) : null}
              <Text style={[styles.policyText, { fontSize: 7.5 }]}>Organizasyon koşulları son sayfada yer almaktadır.</Text>
              {shortNotes ? (
                <>
                  <Text style={[styles.bankTitle, ev.bankNotesTitle]}>NOTLAR</Text>
                  <Text style={ev.bankNotes}>{notes}</Text>
                </>
              ) : null}
            </View>
          </View>
        </View>
      </Page>

      {/* Son sayfa: organizasyon koşulları */}
      <EventTermsPage offer={o} settings={settings} eventSettings={eventSettings} />
    </Document>
  );
};
